// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { LazyImage, mapStateToProps, Props } from "@ces/MobiusOverrides/LazyImage";
import { injectCss } from "@insite/mobius/utilities";
import React from "react";
import { connect } from "react-redux";
import styled, { ThemeContext } from "styled-components";

const LazyPictureStyle = styled.picture<any>`
    max-height: 100%;
    max-width: 100%;
    img {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
    }
    ${injectCss}
`;
/**
 * Xng Picture Component
 */
export class LazyPicture extends LazyImage {
    static contextType = ThemeContext;
    componentDidUpdate(prevProps: Props) {
        if (
            this.props.largeImageSrc !== prevProps.largeImageSrc ||
            this.props.mediumImageSrc !== prevProps.mediumImageSrc ||
            this.props.smallImageSrc !== prevProps.smallImageSrc
        ) {
            this.reloadImage();
        }
    }

    hasInvalidProps() {
        const { largeImageSrc, mediumImageSrc, smallImageSrc } = this.props as Props;
        return !largeImageSrc && !mediumImageSrc && !smallImageSrc;
    }

    renderImage(src?: string, fromPic?: boolean) {
        const { pictureProps, largeImageSrc, mediumImageSrc, smallImageSrc } = this.props as Props;

        return (
            <LazyPictureStyle {...pictureProps}>
                <source
                    media={`(min-width: ${this.context.breakpoints.values[3]}px)`}
                    srcSet={this.getImgUrl(largeImageSrc)}
                />
                <source
                    media={`(min-width: ${this.context.breakpoints.values[2]}px)`}
                    srcSet={this.getImgUrl(mediumImageSrc) || this.getImgUrl(largeImageSrc)}
                />
                <source
                    media={`(max-width: ${this.context.breakpoints.values[2]}px)`}
                    srcSet={this.getImgUrl(smallImageSrc) || this.getImgUrl(largeImageSrc)}
                />
                {super.renderImage(smallImageSrc)}
            </LazyPictureStyle>
        );
    }

    render() {
        const {
            disableLazyLoad,
            isShareEntity,
            pictureProps,
            imgProps,
            altText,
            largeImageSrc,
            mediumImageSrc,
            smallImageSrc,
        } = this.props as Props;

        if (this.hasInvalidProps()) {
            return null;
        }

        if (disableLazyLoad && !isShareEntity) {
            const breakpoints = this.context.breakpoints.values;
            const largeUrl = this.getImgUrl(largeImageSrc);
            const mediumUrl = this.getImgUrl(mediumImageSrc) || largeUrl;
            const smallUrl = this.getImgUrl(smallImageSrc) || mediumUrl || largeUrl;

            return (
                <>
                    <LazyPictureStyle {...pictureProps}>
                        <source media={`(min-width: ${breakpoints[3]}px)`} srcSet={largeUrl} />
                        <source media={`(min-width: ${breakpoints[2]}px)`} srcSet={mediumUrl} />
                        <img width={this.width} height={this.height} alt={altText} src={smallUrl} {...imgProps} />
                    </LazyPictureStyle>
                </>
            );
        }

        return super.render();
    }
}
/** @component */
export default connect(mapStateToProps)(LazyPicture);
